import React, { useState, useContext, useEffect } from "react";
import { Dialog } from "@natera/material/lib/dialog";
import { Button } from "@natera/material/lib/button";
import { defineMessages, useIntl } from "react-intl";
import { PostBillAPIContext } from "@app/postBill/provider/api";
import ThumbsUpIcon from "../../../../assets/svg/icons/thums-up.svg";
import { Svg } from "@natera/material/lib/svg";
import { logMsg } from "@app/utils/logMsg";

import "./surveyModal.scss";

const messages = defineMessages({
  surveyModalHeader: {
    id: "surveyModalHeader",
    defaultMessage: "We would love your feedback.",
  },
  surveyEaseOfPaymentQuestion: {
    id: "surveyEaseOfPaymentQuestion",
    defaultMessage:
      "How easy was it to pay your invoice using our payment portal?",
  },
  surveyEaseOfPaymentVeryDifficult: {
    id: "surveyEaseOfPaymentVeryDifficult",
    defaultMessage: "Very Difficult",
  },
  surveyEaseOfPaymentVeryEasy: {
    id: "surveyEaseOfPaymentVeryEasy",
    defaultMessage: "Very Easy",
  },
  surveyCommentQuestion: {
    id: "surveyCommentQuestion",
    defaultMessage: "In a few words, why did you choose this score?",
  },
  surveyCommentPlaceholder: {
    id: "surveyCommentPlaceholder",
    defaultMessage: "Your feedback (optional)",
  },
  surveyNpsQuestion: {
    id: "surveyNpsQuestion",
    defaultMessage:
      "Based on your experience using our payment portal, how likely are you to recommend Natera?",
  },
  surveyNpsNotLikely: {
    id: "surveyNpsNotLikely",
    defaultMessage: "Not at all likely",
  },
  surveyNpsExtremelyLikely: {
    id: "surveyNpsExtremelyLikely",
    defaultMessage: "Extremely likely",
  },
  surveyNextButton: {
    id: "surveyNextButton",
    defaultMessage: "Next",
  },
  surveySkipButton: {
    id: "surveySkipButton",
    defaultMessage: "Skip",
  },
  surveyCloseButton: {
    id: "surveyCloseButton",
    defaultMessage: "Close",
  },
  surveyThankYouHeader: {
    id: "surveyThankYouHeader",
    defaultMessage: "Thank you!",
  },
  surveyThankYouMessage: {
    id: "surveyThankYouMessage",
    defaultMessage: "We really appreciate your feedback.",
  },
});

type SurveyModalProps = {
  onClose: () => void;
};

enum SurveyStep {
  EASE_OF_PAYMENT = 1,
  COMMENT = 2,
  NPS = 3,
  THANK_YOU = 4,
}

const SurveyModal: React.FC<SurveyModalProps> = ({ onClose }) => {
  const intl = useIntl();
  const { submitSurvey, recordSurveyViewed } = useContext(PostBillAPIContext);

  useEffect(() => {
    recordSurveyViewed();
  }, []);

  const [currentStep, setCurrentStep] = useState<SurveyStep>(
    SurveyStep.EASE_OF_PAYMENT
  );
  const [easeOfPaymentScore, setEaseOfPaymentScore] = useState<number | null>(
    null
  );
  const [easeOfPaymentComment, setEaseOfPaymentComment] = useState<string>("");
  const [npsScore, setNpsScore] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await submitSurvey({
        easeOfPaymentScore,
        easeOfPaymentComment: easeOfPaymentComment.trim() || null,
        npsScore,
      });
      setCurrentStep(SurveyStep.THANK_YOU);
    } catch {
      logMsg("error", "postbill.survey", "Failed to submit survey");
      // Still show thank you screen even if submission fails
      setCurrentStep(SurveyStep.THANK_YOU);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentStep === SurveyStep.EASE_OF_PAYMENT) {
      setCurrentStep(SurveyStep.COMMENT);
    } else if (currentStep === SurveyStep.COMMENT) {
      setCurrentStep(SurveyStep.NPS);
    } else if (currentStep === SurveyStep.NPS) {
      handleSubmit();
    }
  };

  const handleSkip = () => {
    if (currentStep === SurveyStep.COMMENT) {
      setCurrentStep(SurveyStep.NPS);
    }
  };

  const renderScoreSelector = (
    selectedScore: number | null,
    onSelect: (score: number) => void,
    leftLabel: string,
    rightLabel: string
  ) => (
    <div className="survey-modal__score-container">
      <div className="survey-modal__score-buttons">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
          <button
            key={score}
            type="button"
            className={`survey-modal__score-button ${
              selectedScore === score
                ? "survey-modal__score-button--selected"
                : ""
            }`}
            onClick={() => onSelect(score)}
          >
            {score}
          </button>
        ))}
      </div>
      <div className="survey-modal__score-labels">
        <span>{leftLabel}</span>
        <span>{rightLabel}</span>
      </div>
    </div>
  );

  if (currentStep === SurveyStep.THANK_YOU) {
    return (
      <Dialog
        className="survey-modal survey-modal--thank-you"
        onClose={onClose}
        showCrossButton
      >
        <div className="survey-modal__thank-you">
          <h2 className="survey-modal__thank-you-header">
            {intl.formatMessage(messages.surveyThankYouHeader)}
          </h2>
          <span className="survey-modal__thumbs-up-icon">
            <Svg content={ThumbsUpIcon} />
          </span>

          <p className="survey-modal__thank-you-message">
            {intl.formatMessage(messages.surveyThankYouMessage)}
          </p>
          <Button
            className="survey-modal__close-button"
            unelevated
            onClick={onClose}
          >
            {intl.formatMessage(messages.surveyCloseButton)}
          </Button>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      className="survey-modal"
      onClose={onClose}
      showCrossButton
      title={intl.formatMessage(messages.surveyModalHeader)}
    >
      <div className="survey-modal__container">
        {currentStep === SurveyStep.EASE_OF_PAYMENT && (
          <div className="survey-modal__step">
            <p className="survey-modal__question">
              {intl.formatMessage(messages.surveyEaseOfPaymentQuestion)}
            </p>
            {renderScoreSelector(
              easeOfPaymentScore,
              setEaseOfPaymentScore,
              intl.formatMessage(messages.surveyEaseOfPaymentVeryDifficult),
              intl.formatMessage(messages.surveyEaseOfPaymentVeryEasy)
            )}
            <div className="survey-modal__actions">
              <Button
                className="survey-modal__next-button"
                unelevated
                onClick={handleNext}
                disabled={easeOfPaymentScore === null}
              >
                {intl.formatMessage(messages.surveyNextButton)}
              </Button>
            </div>
          </div>
        )}

        {currentStep === SurveyStep.COMMENT && (
          <div className="survey-modal__step">
            <p className="survey-modal__question">
              {intl.formatMessage(messages.surveyCommentQuestion)}
            </p>
            <textarea
              className="survey-modal__comment-field"
              value={easeOfPaymentComment}
              onChange={(e) => setEaseOfPaymentComment(e.target.value)}
              maxLength={2000}
              placeholder={intl.formatMessage(
                messages.surveyCommentPlaceholder
              )}
            />
            <div className="survey-modal__actions">
              <Button
                className="survey-modal__next-button"
                unelevated
                onClick={handleNext}
                disabled={easeOfPaymentComment.length === 0}
              >
                {intl.formatMessage(messages.surveyNextButton)}
              </Button>
              <button
                type="button"
                className="survey-modal__skip-button"
                onClick={handleSkip}
              >
                {intl.formatMessage(messages.surveySkipButton)}
              </button>
            </div>
          </div>
        )}

        {currentStep === SurveyStep.NPS && (
          <div className="survey-modal__step">
            <p className="survey-modal__question">
              {intl.formatMessage(messages.surveyNpsQuestion)}
            </p>
            {renderScoreSelector(
              npsScore,
              setNpsScore,
              intl.formatMessage(messages.surveyNpsNotLikely),
              intl.formatMessage(messages.surveyNpsExtremelyLikely)
            )}
            <div className="survey-modal__actions">
              <Button
                className="survey-modal__next-button"
                unelevated
                onClick={handleNext}
                disabled={npsScore === null || isSubmitting}
              >
                {intl.formatMessage(messages.surveyNextButton)}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default SurveyModal;
